import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { Form, Modal, InputPassword, Button } from '../ui';
import renderNotification from '../../helpers/notifications/renderNotification';
import PasswordRules from '../../containers/Login/PasswordRules';
import { NOTIFICATION_MESSAGES } from '../../localization/Notifications/dictionary';
import { DEFAULT_RULES } from '../../consts/formRules';
import { selectChangePasswordLoading } from '../../redux/password/selectors';

const TopbarModalFooterWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`;

const TopbarFormWrapper = styled.div`
  margin-bottom: 30px;
  padding: 20px 40px 0;

  label {
    &::after {
      display: none;
    }
  }

  .ant-form-item {
    &:last-child {
      margin: 0;
    }
  }
`;

/**
 * Functional component that renders the modal for changing the user password.
 * Includes form fields for current and new passwords, and handles form submission
 * and validation. Displays a footer with buttons for cancelling or saving the changes.
 * @property {Function} changePassword - a function that triggers the password change process.
 * @property {Function} onCancel - a function that closes the modal without saving changes.
 * @property {String} userId - the ID of the user whose password is being changed.
 * @property {Boolean} visible - a boolean indicating whether the modal is visible.
 * @property {Boolean} changePasswordFailed - a boolean indicating if there was an error during password change.
 * @property {Boolean} changePasswordLoading - a boolean indicating if the password change process is loading.
 */
const TopbarUserChangePasswordModal = ({
  changePassword,
  onCancel,
  userId,
  visible,
  changePasswordFailed,
  changePasswordLoading
}) => {
  const [form] = Form.useForm();
  const { resetFields, validateFields } = form;
  const currentPassword = Form.useWatch('currentPassword', form);
  const newPassword = Form.useWatch('newPassword', form);
  const [isFieldsTouched, setIsFieldsTouched] = useState(false);
  const [newPasswordValue, setNewPasswordValue] = useState('');

  // Cleanup form fields when the component unmounts
  useEffect(() => {
    return () => {
      resetFields();
    };
  }, [form]);

  const isFormCompleted = currentPassword && newPassword;

  // Handle form submission and password change logic
  const handleSubmit = () =>
    validateFields()
      .then(({ newPassword, currentPassword }) => {
        if (changePasswordLoading) {
          return;
        }
        if (newPassword) {
          changePassword({ currentPassword, newPassword, userId, callback: onCancel });
        } else {
          const { password_change_current_password_incorrect } = NOTIFICATION_MESSAGES;
          return renderNotification({
            type: 'error',
            message: password_change_current_password_incorrect
          });
        }
      })
      .catch(() => {});

  const handleFormValuesChange = (changedValues) => {
    setIsFieldsTouched(Math.random());
    if (changedValues.newPassword) {
      setNewPasswordValue(changedValues.newPassword);
    }
  };

  const footer = (
    <TopbarModalFooterWrapper>
      <Button type="secondary" onClick={onCancel}>
        Abbrechen
      </Button>
      <Button
        disabled={!isFieldsTouched || (isFieldsTouched && !isFormCompleted)}
        type="primary"
        onClick={handleSubmit}
      >
        Speichern
      </Button>
    </TopbarModalFooterWrapper>
  );

  return (
    <Modal
      className="modal-no-padding"
      title="Passwort ändern"
      open={visible}
      mask={{ closable: true }}
      onCancel={onCancel}
      footer={footer}
    >
      <Form form={form} style={{ marginBottom: 26 }} onValuesChange={handleFormValuesChange}>
        <TopbarFormWrapper>
          <Form.Item label="Aktuelles Passwort" name="currentPassword" rules={DEFAULT_RULES.INPUT_REQUIRED}>
            <InputPassword status={changePasswordFailed ? 'error' : ''} />
          </Form.Item>
          <Form.Item label="Neues Passwort" name="newPassword" rules={DEFAULT_RULES.INPUT_REQUIRED}>
            <InputPassword status={changePasswordFailed ? 'error' : ''} />
          </Form.Item>
          <PasswordRules password={newPasswordValue} />
        </TopbarFormWrapper>
      </Form>
    </Modal>
  );
};

const mapStateToProps = (state) => ({
  changePasswordLoading: selectChangePasswordLoading(state)
});

export default connect(mapStateToProps)(TopbarUserChangePasswordModal);
